
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { db, DocumentReference } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNIT_TYPE } from '@run-diffusion/shared';
import { FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_NODES_LIMIT } from '@/views/Runnits/constants';
import { TEMPLATE_MAX_RESULTS_PER_NODE } from '@/mixins/TemplateGenerationResultsMixin';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';

export default Vue.extend({
	name: 'DeleteRunnitBoardDialog',
	data () {
		return {
			deleting: false,
		};
	},
	computed: {
		...mapState([
			'runnitState',
		]),
		isTemplate () {
			return this.runnitState.runnit?.type === RUNNIT_TYPE.TEMPLATE;
		},
		entityName () {
			return this.isTemplate ? 'Template' : 'Board';
		},
		runnitTitle () {
			return this.runnitState.runnit?.title || `Untitled ${this.entityName}`;
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		onClose () {
			this.updateRunnitState({
				runnitDeleteOpen: false,
			});
		},
		async handleDeleteConfirm (closeAndResetCallback: Function) {
			if (!this.runnitState.runnit) {
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
				this.onClose();
				return;
			}

			try {
				this.deleting = true;

				const runnitId = this.runnitState.runnit.id;
				const runnitRef: DocumentReference = db.doc(`runnits/${runnitId}`);
				const deletedAt = new Date();

				// If it's a template, mark all associated nodes, nodeRuns, and nodeRunResults as deleted
				if (this.isTemplate) {
					await this.deleteTemplateAssociations(runnitId, deletedAt);
				}

				// Mark the runnit/template as deleted
				await runnitRef.update({
					deletedAt,
				});

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! ${this.entityName} deleted`,
					show: true,
				});
			} catch (e) {
				console.error('Error deleting runnit:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error! Problem deleting the ${this.entityName}, please try again.`,
					show: true,
				});
			} finally {
				this.deleting = false;
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
				this.onClose();
			}
		},
		async deleteTemplateAssociations (templateId: string, deletedAt: Date): Promise<void> {
			let batch = db.batch();
			let batchCount = 0;
			const BATCH_LIMIT = 500;

			// 1. Mark all nodes as deleted
			const nodesSnapshot = await db
				.collection(`runnits/${templateId}/runnitNodes`)
				.where('deletedAt', '==', null)
				.get();

			for (const nodeDoc of nodesSnapshot.docs) {
				batch.update(nodeDoc.ref, { deletedAt });
				batchCount++;

				if (batchCount >= BATCH_LIMIT) {
					await batch.commit();
					batch = db.batch();
					batchCount = 0;
				}
			}

			// 2. Mark all nodeRuns and their results as deleted
			const maxNodeRunsOnTemplate = FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_NODES_LIMIT * TEMPLATE_MAX_RESULTS_PER_NODE;
			const nodeRunsSnapshot = await db
				.collection('runnitNodeRuns')
				.where('runnitId', '==', templateId)
				.where('deletedAt', '==', null)
				.limit(maxNodeRunsOnTemplate)
				.get();

			for (const nodeRunDoc of nodeRunsSnapshot.docs) {
				const nodeRun = {
					...nodeRunDoc.data(),
					id: nodeRunDoc.id,
				} as any;

				const nodeRunRef = db.doc(`runnitNodeRuns/${nodeRun.id}`);

				// Mark all results as deleted
				const results = nodeRun.results || [];
				const updatedResults = results.map((result: any) => ({
					...result,
					deletedAt: result.deletedAt || deletedAt, // Only set if not already deleted
				}));

				// Mark the nodeRun as deleted
				batch.update(nodeRunRef, {
					results: updatedResults,
					deletedAt,
				});
				batchCount++;

				if (batchCount >= BATCH_LIMIT) {
					await batch.commit();
					batch = db.batch();
					batchCount = 0;
				}
			}

			// Commit any remaining updates
			if (batchCount > 0) {
				await batch.commit();
			}
		},
	},
	components: {
		DoubleConfirmDangerDialog,
	},
});
